import type { LiveServerMessage, PublicSnapshot } from "../../shared/contracts";
import { fetchSnapshot } from "./api";
import { liveServerMessageSchema } from "./schema";

export type ConnectionState = "connecting" | "live" | "polling" | "offline";

export interface LiveConnectionHandlers {
  onSnapshot(snapshot: PublicSnapshot): void;
  onState(state: ConnectionState): void;
}

export class LiveConnection {
  private socket: WebSocket | null = null;
  private pollTimer: number | null = null;
  private reconnectTimer: number | null = null;
  private controller: AbortController | null = null;
  private resyncPromise: Promise<void> | null = null;
  private session = 0;
  private stopped = true;
  private sequence = -1;
  private generatedAtMs = Number.NEGATIVE_INFINITY;
  private reconnectAttempt = 0;

  constructor(private readonly handlers: LiveConnectionHandlers) {}

  start(): void {
    if (!this.stopped) return;
    this.stopped = false;
    this.retry();
  }

  stop(): void {
    this.stopped = true;
    this.session += 1;
    this.controller?.abort();
    this.controller = null;
    this.resyncPromise = null;
    const socket = this.socket;
    this.socket = null;
    socket?.close();
    this.clearPoll();
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  retry(): void {
    if (this.stopped) return;
    const session = ++this.session;
    this.controller?.abort();
    this.resyncPromise = null;
    const socket = this.socket;
    this.socket = null;
    socket?.close();
    this.clearPoll();
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.reconnectAttempt = 0;
    this.handlers.onState("connecting");
    void this.resync().finally(() => {
      if (!this.stopped && session === this.session) this.openSocket();
    });
  }

  private resync(): Promise<void> {
    if (this.stopped) return Promise.resolve();
    if (this.resyncPromise) return this.resyncPromise;
    const controller = new AbortController();
    this.controller = controller;
    this.resyncPromise = fetchSnapshot(controller.signal)
      .then((snapshot) => {
        if (this.stopped || controller.signal.aborted) return;
        this.acceptSnapshot(snapshot);
        if (this.pollTimer !== null) this.handlers.onState("polling");
      })
      .catch(() => {
        if (this.stopped || controller.signal.aborted) return;
        this.beginPolling();
        this.handlers.onState("offline");
      })
      .finally(() => {
        if (this.controller !== controller) return;
        this.controller = null;
        this.resyncPromise = null;
      });
    return this.resyncPromise;
  }

  private acceptSnapshot(snapshot: PublicSnapshot): void {
    // HTTP and socket payloads are validated at their respective boundaries.
    const generatedAtMs = Date.parse(snapshot.generatedAt);
    if (snapshot.sequence < this.sequence ||
      (snapshot.sequence === this.sequence && generatedAtMs <= this.generatedAtMs)) return;
    this.sequence = snapshot.sequence;
    this.generatedAtMs = generatedAtMs;
    this.handlers.onSnapshot(snapshot);
  }

  private openSocket(): void {
    if (this.stopped || typeof WebSocket === "undefined") {
      this.beginPolling();
      return;
    }
    const previous = this.socket;
    this.socket = null;
    previous?.close();
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    let socket: WebSocket;
    try {
      socket = new WebSocket(`${protocol}//${window.location.host}/api/v1/live?contract=9`);
    } catch {
      this.beginPolling();
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;

    socket.addEventListener("open", () => {
      if (this.stopped || socket !== this.socket) return;
      this.reconnectAttempt = 0;
      this.clearPoll();
      this.handlers.onState("live");
    });

    socket.addEventListener("message", (event) => {
      if (this.stopped || socket !== this.socket) return;
      const message = parseMessage(event.data);
      if (!message) return;
      if (message.type === "resync") {
        void this.resync();
        return;
      }
      const incomingSequence = message.type === "update" ? message.sequence : message.snapshot.sequence;
      if (message.type === "update" && incomingSequence !== message.snapshot.sequence) return;
      if (message.type === "update" && this.sequence >= 0 && incomingSequence > this.sequence + 1) {
        void this.resync();
        return;
      }
      if (this.pollTimer !== null) {
        this.clearPoll();
        this.handlers.onState("live");
      }
      this.acceptSnapshot(message.snapshot);
    });

    socket.addEventListener("close", () => this.handleSocketLoss(socket));
    socket.addEventListener("error", () => socket.close());
  }

  private handleSocketLoss(socket: WebSocket): void {
    if (this.stopped || socket !== this.socket) return;
    this.socket = null;
    this.handlers.onState("polling");
    this.beginPolling();
    this.scheduleReconnect();
  }

  private beginPolling(): void {
    if (this.stopped || this.pollTimer !== null) return;
    this.handlers.onState("polling");
    this.pollTimer = window.setInterval(() => void this.resync(), 15_000);
  }

  private clearPoll(): void {
    if (this.pollTimer !== null) window.clearInterval(this.pollTimer);
    this.pollTimer = null;
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectTimer !== null) return;
    const delay = Math.min(30_000, 1_500 * 2 ** this.reconnectAttempt) + Math.random() * 700;
    this.reconnectAttempt += 1;
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.openSocket();
    }, delay);
  }
}

function parseMessage(value: unknown): LiveServerMessage | null {
  if (typeof value !== "string") return null;
  try {
    const parsed = liveServerMessageSchema.safeParse(JSON.parse(value));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
